import React, { useEffect, useState } from 'react';
import { deleteClientByEmail, getClient, updateClientById } from '../../../services/Service';
import { Button, Modal, Table } from 'react-bootstrap';
import * as XLSX from 'xlsx';
import { FaEye, FaTrash } from 'react-icons/fa';
import { FiAlertCircle, FiCalendar, FiCheckCircle, FiSearch, FiUsers, FiX } from 'react-icons/fi';

const DATE_FILTERS = {
  ALL: 'all',
  TODAY: 'today',
  WEEK: 'week',
  MONTH: 'month',
};

const STATUS_FILTERS = {
  ALL: 'all',
  ACTIVE: 'active',
  INACTIVE: 'inactive',
};

const hasAdminToken = (client) => {
  const token = client.admin_token;
  return typeof token === 'string' ? token.trim() !== '' : Boolean(token);
};

const getActivationState = (client) => {
  return hasAdminToken(client)
    ? { label: 'Active', variant: 'success' }
    : { label: 'Inactive', variant: 'danger' };
};

const getRoleLabel = (role) => {
  if (role === 'T') return 'Ticketbot';
  if (role === 'L') return 'Leadbot';
  if (role === 'C') return 'Client Admin';
  return '-';
};

const getRegistrationDate = (client) => client.admin_created_at || client.created_at || null;

const parseDate = (value) => {
  if (!value) return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();

    const mysqlMatch = trimmed.match(
      /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?$/
    );
    if (mysqlMatch) {
      const [, year, month, day, hour = '0', minute = '0', second = '0'] = mysqlMatch;
      return new Date(
        Number(year),
        Number(month) - 1,
        Number(day),
        Number(hour),
        Number(minute),
        Number(second)
      );
    }

    const formattedMatch = trimmed.match(/^(\d{2})\s([A-Za-z]{3})\s(\d{4})$/);
    if (formattedMatch) {
      const [, day, monthLabel, year] = formattedMatch;
      const monthIndex = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
        .indexOf(monthLabel);

      if (monthIndex !== -1) {
        return new Date(Number(year), monthIndex, Number(day));
      }
    }
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatDate = (value) => {
  const parsed = parseDate(value);
  if (!parsed) return '-';

  return new Intl.DateTimeFormat('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(parsed);
};

const isSameDay = (left, right) =>
  left.getFullYear() === right.getFullYear() &&
  left.getMonth() === right.getMonth() &&
  left.getDate() === right.getDate();

const getStartOfWeek = (date) => {
  const start = new Date(date);
  const day = start.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  start.setDate(start.getDate() + diff);
  start.setHours(0, 0, 0, 0);
  return start;
};

const matchesDateFilter = (client, filter) => {
  if (filter === DATE_FILTERS.ALL) return true;

  const clientDate = parseDate(getRegistrationDate(client));
  if (!clientDate) return false;

  const now = new Date();

  if (filter === DATE_FILTERS.TODAY) {
    return isSameDay(clientDate, now);
  }

  if (filter === DATE_FILTERS.WEEK) {
    const weekStart = getStartOfWeek(now);
    const nextWeekStart = new Date(weekStart);
    nextWeekStart.setDate(nextWeekStart.getDate() + 7);
    return clientDate >= weekStart && clientDate < nextWeekStart;
  }

  if (filter === DATE_FILTERS.MONTH) {
    return (
      clientDate.getFullYear() === now.getFullYear() &&
      clientDate.getMonth() === now.getMonth()
    );
  }

  return true;
};

const getRowHighlightClass = (client) => {
  const activationState = getActivationState(client);
  return activationState.label === 'Inactive' ? 'table-danger' : '';
};

const matchesStatusFilter = (client, filter) => {
  if (filter === STATUS_FILTERS.ALL) return true;
  const statusLabel = getActivationState(client).label;
  if (filter === STATUS_FILTERS.ACTIVE) return statusLabel === 'Active';
  if (filter === STATUS_FILTERS.INACTIVE) return statusLabel === 'Inactive';
  return true;
};

const AllClient = () => {
  const [clients, setClients] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [dateFilter, setDateFilter] = useState(DATE_FILTERS.ALL);
  const [statusFilter, setStatusFilter] = useState(STATUS_FILTERS.ALL);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClient, setSelectedClient] = useState(null);
  const [showClientModal, setShowClientModal] = useState(false);
  const [isModalEditing, setIsModalEditing] = useState(false);
  const [modalForm, setModalForm] = useState({
    name: '',
    slug: '',
    email: '',
    contact: '',
    role: 'T',
    status: 'active',
  });
  const [modalErrors, setModalErrors] = useState({
    slug: '',
    email: '',
    contact: '',
  });

  const [toastMessage, setToastMessage] = useState('Client updated successfully!');
  const [toastVariant, setToastVariant] = useState('success');
  const [showToast, setShowToast] = useState(false);
  const [statusUpdatingId, setStatusUpdatingId] = useState(null);

  const itemsPerPage = 10;

  useEffect(() => {
    fetchClients();

    const refreshHandler = () => {
      fetchClients();
    };

    window.addEventListener('refreshClients', refreshHandler);

    return () => {
      window.removeEventListener('refreshClients', refreshHandler);
    };
  }, []);

  useEffect(() => {
    if (!showToast) return undefined;
    const timer = window.setTimeout(() => {
      setShowToast(false);
    }, 3200);
    return () => window.clearTimeout(timer);
  }, [showToast]);

  const fetchClients = async () => {
    const response = await getClient();
    if (response.status === 'success') {
      setClients(response.data || []);
    } else {
      console.error('Failed to fetch clients:', response.message);
    }
  };

  const triggerToast = (variant, message) => {
    setToastVariant(variant);
    setToastMessage(message);
    setShowToast(true);
  };

  const openClientDetails = (client) => {
    setSelectedClient(client);
    setShowClientModal(true);
    setIsModalEditing(false);
    setModalErrors({ slug: '', email: '', contact: '' });
    setModalForm({
      name: client.Name || '',
      slug: client.client_slug || '',
      email: client.admin_email || client.Email || '',
      contact: client.admin_mobile || client.ContactNumber || '',
      role: client.admin_role || 'T',
      status: hasAdminToken(client) ? 'active' : 'inactive',
    });
  };

  const closeClientModal = () => {
    setShowClientModal(false);
    setIsModalEditing(false);
    setModalErrors({ slug: '', email: '', contact: '' });
  };

  const handleModalSave = async () => {
    if (!selectedClient) return;

    const normalizedSlug = modalForm.slug.trim().toLowerCase();
    const trimmedEmail = modalForm.email.trim().toLowerCase();
    const trimmedContact = modalForm.contact.trim();
    const isValidSlug = /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(normalizedSlug);
    const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail);
    const isValidContact = /^[6-9]\d{9}$/.test(trimmedContact);

    const nextErrors = { slug: '', email: '', contact: '' };
    let hasError = false;

    if (!isValidSlug) {
      nextErrors.slug = 'Enter valid slug like atai-leadbot-pune.';
      hasError = true;
    }
    if (!isValidEmail) {
      nextErrors.email = 'Enter a valid email address.';
      hasError = true;
    }
    if (!isValidContact) {
      nextErrors.contact = 'Enter a valid 10-digit mobile number (6-9 start).';
      hasError = true;
    }

    setModalErrors(nextErrors);
    if (hasError) return;

    try {
      await updateClientById({
        client_id: selectedClient.id,
        client_name: modalForm.name.trim() || selectedClient.Name || '',
        client_slug: normalizedSlug,
        client_email: trimmedEmail,
        client_contact_number: trimmedContact,
        user_role_abbr: modalForm.role,
        is_active: (modalForm.status || 'active') === 'active' ? 1 : 0,
        status: modalForm.status || 'active',
        client_status: modalForm.status || 'active',
        admin_token:
          (modalForm.status || 'active') === 'active'
            ? (selectedClient.admin_token && String(selectedClient.admin_token).trim())
              || `activated-${selectedClient.id}`
            : '',
      });

      await fetchClients();
      const updatedClient = {
        ...selectedClient,
        Name: modalForm.name.trim() || selectedClient.Name || '',
        client_slug: normalizedSlug,
        admin_email: trimmedEmail,
        admin_mobile: trimmedContact,
        admin_role: modalForm.role,
        admin_token:
          (modalForm.status || 'active') === 'active'
            ? (selectedClient.admin_token && String(selectedClient.admin_token).trim())
              || `activated-${selectedClient.id}`
            : '',
      };
      setSelectedClient(updatedClient);
      setIsModalEditing(false);
      triggerToast('success', 'Client updated successfully!');
    } catch (error) {
      console.error('Failed to update client:', error);
      triggerToast('danger', error?.response?.data?.message || 'Failed to update client.');
    }
  };

  const handleToggleClientStatus = async (client) => {
    const currentlyActive = hasAdminToken(client);
    const nextStatus = currentlyActive ? 'inactive' : 'active';
    const confirmationText = currentlyActive
      ? `Mark ${client.Name || 'this client'} as Inactive?`
      : `Mark ${client.Name || 'this client'} as Active?`;

    if (!window.confirm(confirmationText)) return;

    try {
      setStatusUpdatingId(client.id);
      await updateClientById({
        client_id: client.id,
        client_name: client.Name || '',
        client_slug: (client.client_slug || '').trim().toLowerCase(),
        client_email: (client.admin_email || client.Email || '').trim().toLowerCase(),
        client_contact_number: (client.admin_mobile || client.ContactNumber || '').trim(),
        user_role_abbr: client.admin_role || 'T',
        is_active: nextStatus === 'active' ? 1 : 0,
        status: nextStatus,
        client_status: nextStatus,
        admin_token:
          nextStatus === 'active'
            ? (client.admin_token && String(client.admin_token).trim()) || `activated-${client.id}`
            : '',
      });
      await fetchClients();

      if (selectedClient?.id === client.id) {
        setSelectedClient((prev) => (prev
          ? {
            ...prev,
            admin_token:
                nextStatus === 'active'
                  ? (client.admin_token && String(client.admin_token).trim()) || `activated-${client.id}`
                  : '',
          }
          : prev));
      }

      triggerToast('success', `Client marked as ${nextStatus === 'active' ? 'Active' : 'Inactive'}.`);
    } catch (error) {
      console.error('Failed to update client status:', error);
      triggerToast(
        'danger',
        error?.response?.data?.message || 'Unable to change client status right now.'
      );
    } finally {
      setStatusUpdatingId(null);
    }
  };

  const handleDeleteClient = async (client) => {
    const clientName = client.Name || 'this client';
    const confirmed = window.confirm(`Delete ${clientName}?`);

    if (!confirmed) return;

    try {
      const clientEmail = (client.admin_email || client.Email || '').trim();
      const response = await deleteClientByEmail(clientEmail);

      if (response?.status && response.status !== 'success') {
        triggerToast('danger', response?.message || 'Unable to delete client.');
        return;
      }

      await fetchClients();
      triggerToast('success', 'Client deleted successfully!');
    } catch (error) {
      console.error('Failed to delete client:', error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to delete client.';
      triggerToast('danger', errorMessage);
    }
  };

  const normalizedSearch = searchTerm.trim().toLowerCase();
  const filteredClients = clients
    .filter((client) => {
      if (!matchesDateFilter(client, dateFilter)) return false;
      if (!matchesStatusFilter(client, statusFilter)) return false;

      if (!normalizedSearch) return true;

      const searchable = [
        client.Name,
        client.client_slug,
        client.admin_email || client.Email,
        client.admin_mobile || client.ContactNumber,
        getRoleLabel(client.admin_role),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return searchable.includes(normalizedSearch);
    })
    .sort((left, right) => {
      const leftDate = parseDate(getRegistrationDate(left));
      const rightDate = parseDate(getRegistrationDate(right));
      return (rightDate?.getTime() || 0) - (leftDate?.getTime() || 0);
    });

  const totalVisibleCount = clients.length;
  const totalActiveCount = clients.filter(
    (client) => getActivationState(client).label === 'Active'
  ).length;
  const totalInactiveCount = clients.length - totalActiveCount;
  const currentStatusFilterLabel =
    statusFilter === STATUS_FILTERS.ACTIVE
      ? 'Active'
      : statusFilter === STATUS_FILTERS.INACTIVE
        ? 'Inactive'
        : 'All';

  const exportToExcel = () => {
    const exportRows = filteredClients.map((client) => ({
      ID: client.id,
      Name: client.Name,
      Slug: client.client_slug,
      Email: client.admin_email || client.Email,
      Contact: client.admin_mobile || client.ContactNumber,
      Type: getRoleLabel(client.admin_role),
      RegisteredOn: formatDate(getRegistrationDate(client)),
      Status: getActivationState(client).label,
      Employees: client.NoofEmp || 0,
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Clients');
    XLSX.writeFile(workbook, 'AllClients.xlsx');
  };

  const indexOfLast = currentPage * itemsPerPage;
  const indexOfFirst = indexOfLast - itemsPerPage;
  const currentClients = filteredClients.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.max(Math.ceil(filteredClients.length / itemsPerPage), 1);
  const isSuccessToast = toastVariant === 'success';
  const toastTitle = isSuccessToast ? 'Success' : 'Action Failed';

  return (
    <div className="container-fluid px-0 px-md-2 mt-3 all-client-page">
      <div className="card border-0 shadow-sm rounded-4 overflow-hidden all-client-shell">
        <div className="card-body p-3 p-md-3">
          <div className="all-client-hero rounded-4 p-3 mb-3">
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
              <div>
                <div className="all-client-kicker">Super Admin Workspace</div>
                <h4 className="mb-1 fw-bold">All Clients</h4>
                <p className="text-muted mb-0">
                  Review registrations, filter by date, update client details, and manage role or status.
                </p>
              </div>
              <Button variant="success" onClick={exportToExcel} className="rounded-pill px-3">
                Export to Excel
              </Button>
            </div>
          </div>

          <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
            <div>
              <div className="text-muted small">
                Current status filter: <span className="fw-semibold text-dark">{currentStatusFilterLabel}</span>
              </div>
            </div>
            <div className="text-muted small">
              Matching rows after all filters: <span className="fw-semibold text-dark">{filteredClients.length}</span>
            </div>
          </div>

          <div className="row g-2 mb-3 all-client-stat-grid">
            <div className="col-md-4">
              <button
                type="button"
                className={`w-100 text-start rounded-4 border p-2 h-100 btn all-client-stat-card ${
                  statusFilter === STATUS_FILTERS.ALL ? 'btn-primary text-white' : 'bg-light'
                }`}
                onClick={() => {
                  setStatusFilter(STATUS_FILTERS.ALL);
                  setCurrentPage(1);
                }}
              >
                <div className="text-muted text-uppercase fw-semibold all-client-stat-label">Visible Clients</div>
                <div className="fw-bold all-client-stat-number">{totalVisibleCount}</div>
              </button>
            </div>
            <div className="col-md-4">
              <button
                type="button"
                className={`w-100 text-start rounded-4 border p-2 h-100 btn all-client-stat-card ${
                  statusFilter === STATUS_FILTERS.ACTIVE ? 'btn-success text-white' : 'bg-light'
                }`}
                onClick={() => {
                  setStatusFilter(STATUS_FILTERS.ACTIVE);
                  setCurrentPage(1);
                }}
              >
                <div className="text-muted text-uppercase fw-semibold all-client-stat-label">Active</div>
                <div className="fw-bold all-client-stat-number">{totalActiveCount}</div>
              </button>
            </div>
            <div className="col-md-4">
              <button
                type="button"
                className={`w-100 text-start rounded-4 border p-2 h-100 btn all-client-stat-card ${
                  statusFilter === STATUS_FILTERS.INACTIVE ? 'btn-danger text-white' : 'bg-light'
                }`}
                onClick={() => {
                  setStatusFilter(STATUS_FILTERS.INACTIVE);
                  setCurrentPage(1);
                }}
              >
                <div className="text-muted text-uppercase fw-semibold all-client-stat-label">Inactive</div>
                <div className="fw-bold all-client-stat-number">{totalInactiveCount}</div>
              </button>
            </div>
          </div>

          <div className="row g-2 align-items-end mb-2">
            <div className="col-lg-4">
              <label className="form-label fw-semibold">Search</label>
              <div className="input-group">
                <span className="input-group-text bg-white border-end-0">
                  <FiSearch size={14} />
                </span>
                <input
                  type="text"
                  className="form-control border-start-0"
                  placeholder="Search by name, slug, email, contact, or type"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                />
              </div>
            </div>
            <div className="col-lg-5">
              <label className="form-label fw-semibold">Registration Filter</label>
              <div className="d-flex flex-wrap gap-2">
                <button
                  type="button"
                  className={`btn btn-sm ${dateFilter === DATE_FILTERS.ALL ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => {
                    setDateFilter(DATE_FILTERS.ALL);
                    setCurrentPage(1);
                  }}
                >
                  All
                </button>
                <button
                  type="button"
                  className={`btn btn-sm ${dateFilter === DATE_FILTERS.TODAY ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => {
                    setDateFilter(DATE_FILTERS.TODAY);
                    setCurrentPage(1);
                  }}
                >
                  Today
                </button>
                <button
                  type="button"
                  className={`btn btn-sm ${dateFilter === DATE_FILTERS.WEEK ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => {
                    setDateFilter(DATE_FILTERS.WEEK);
                    setCurrentPage(1);
                  }}
                >
                  This Week
                </button>
                <button
                  type="button"
                  className={`btn btn-sm ${dateFilter === DATE_FILTERS.MONTH ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => {
                    setDateFilter(DATE_FILTERS.MONTH);
                    setCurrentPage(1);
                  }}
                >
                  This Month
                </button>
              </div>
            </div>
            <div className="col-lg-3">
              <div className="rounded-4 border bg-light p-2 h-100">
                <div className="fw-semibold mb-1">Status Filter</div>
                <div className="small text-muted">
                  Use the top cards with date and search.
                </div>
              </div>
            </div>
          </div>

          <div className="all-client-data-zone position-relative">
            <div className="d-xl-none">
              {currentClients.length > 0 ? (
                <div className="mobile-client-grid">
                  {currentClients.map((client) => {
                    const activationState = getActivationState(client);

                    return (
                      <article key={`mobile-${client.id}`} className={`mobile-client-card ${activationState.label === 'Inactive' ? 'mobile-client-card-inactive' : ''}`}>
                        <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                          <div>
                            <div className="small text-muted">#{client.id}</div>
                            <h6 className="mb-1">{client.Name || '-'}</h6>
                          </div>
                          <span className={`badge ${activationState.label === 'Active' ? 'text-bg-success' : 'text-bg-danger'}`}>
                            {activationState.label}
                          </span>
                        </div>

                        <div className="mobile-client-meta mb-3">
                          <div className="mobile-client-meta-item">
                            <FiCalendar size={14} />
                            <span>{formatDate(getRegistrationDate(client))}</span>
                          </div>
                          <div className="mobile-client-meta-item">
                            <FiUsers size={14} />
                            <span>{client.NoofEmp || 0} employees</span>
                          </div>
                          <div>
                            <span className="badge text-bg-info">{getRoleLabel(client.admin_role)}</span>
                          </div>
                        </div>

                        <div className="d-flex justify-content-between align-items-center">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            className="rounded-pill px-2 py-1"
                            onClick={() => openClientDetails(client)}
                          >
                            <FaEye className="me-1" />
                            View details
                          </Button>
                          <div className="d-flex align-items-center gap-2">
                            <button
                              type="button"
                              className={`btn btn-sm rounded-pill px-2 py-1 ${activationState.label === 'Active' ? 'btn-outline-danger' : 'btn-outline-success'}`}
                              onClick={() => handleToggleClientStatus(client)}
                              disabled={statusUpdatingId === client.id}
                            >
                              {statusUpdatingId === client.id
                                ? '...'
                                : activationState.label === 'Active'
                                  ? 'Deactivate'
                                  : 'Activate'}
                            </button>
                            <button type="button" className="btn btn-link text-danger p-0" onClick={() => handleDeleteClient(client)}>
                              <FaTrash size={17} />
                            </button>
                          </div>
                        </div>
                      </article>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center text-muted py-4">No clients found for the selected filter.</div>
              )}
            </div>

            <div className="table-responsive d-none d-xl-block all-client-table-wrap">
              <Table striped bordered hover className="align-middle all-client-table mb-0">
                <thead className="table-dark text-center">
                  <tr>
                    <th>#</th>
                    <th>Organization Name</th>
                    <th>Type</th>
                    <th>Registered On</th>
                    <th>Activation Status</th>
                    {/* <th>Set Status</th> */}
                    <th>No. of Employees</th>
                    <th>Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {currentClients.length > 0 ? currentClients.map((client) => {
                    const activationState = getActivationState(client);

                    return (
                      <tr key={client.id} className={getRowHighlightClass(client)}>
                        <td className="text-center">{client.id}</td>
                        <td>{client.Name || '-'}</td>
                        <td className="text-center">
                          <span className="badge text-bg-info">
                            {getRoleLabel(client.admin_role)}
                          </span>
                        </td>
                        <td className="text-center">{formatDate(getRegistrationDate(client))}</td>
                        <td className="text-center">
                          <span className={`badge text-bg-${activationState.variant}`}>
                            {activationState.label}
                          </span>
                        </td>
                        {/* <td className="text-center">
                          <Button
                            size="sm"
                            variant={activationState.label === 'Active' ? 'outline-danger' : 'outline-success'}
                            className="px-2 py-1"
                            onClick={() => handleToggleClientStatus(client)}
                            disabled={statusUpdatingId === client.id}
                          >
                            {statusUpdatingId === client.id
                              ? 'Updating...'
                              : activationState.label === 'Active'
                                ? 'Deactivate'
                                : 'Activate'}
                          </Button>
                        </td> */}
                        <td className="text-center">{client.NoofEmp || 0}</td>
                        <td className="text-center">
                          <div className="d-flex justify-content-center gap-2">
                            <Button
                              size="sm"
                              onClick={() => openClientDetails(client)}
                            >
                              <FaEye className="me-1" />
                            </Button>
                            <Button
                              variant="link"
                              size="sm"
                              className="text-danger p-0"
                              onClick={() => handleDeleteClient(client)}
                            >
                              <FaTrash size={18} />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  }) : (
                      <tr>
                      <td colSpan="8" className="text-center text-muted py-4">
                        No clients found for the selected filter.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>

            {showToast ? (
              <div className="table-feedback-layer" role="status" aria-live="polite">
                <div className={`table-feedback-popup ${isSuccessToast ? 'table-feedback-popup-success' : 'table-feedback-popup-danger'}`}>
                  <div className="table-feedback-icon">
                    {isSuccessToast ? <FiCheckCircle size={20} /> : <FiAlertCircle size={20} />}
                  </div>
                  <div className="table-feedback-content">
                    <div className="table-feedback-title">{toastTitle}</div>
                    <div className="table-feedback-text">{toastMessage}</div>
                  </div>
                  <button
                    type="button"
                    className="table-feedback-close"
                    aria-label="Close message"
                    onClick={() => setShowToast(false)}
                  >
                    <FiX size={16} />
                  </button>
                </div>
              </div>
            ) : null}
          </div>

          <div className="d-flex justify-content-center align-items-center gap-2 mt-3 flex-wrap">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNumber) => (
              <button
                key={pageNumber}
                className={`btn btn-sm ${
                  currentPage === pageNumber ? 'btn-primary' : 'btn-outline-primary'
                }`}
                onClick={() => setCurrentPage(pageNumber)}
              >
                {pageNumber}
              </button>
            ))}
          </div>
        </div>
      </div>

      <Modal
        show={showClientModal}
        onHide={closeClientModal}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Client Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedClient ? (
            <div className="row g-3">
              <div className="col-md-6">
                <div className="client-detail-label">Organization Name</div>
                {isModalEditing ? (
                  <input
                    type="text"
                    className="form-control"
                    value={modalForm.name}
                    onChange={(e) => setModalForm((prev) => ({ ...prev, name: e.target.value }))}
                  />
                ) : (
                  <div className="client-detail-value">{selectedClient.Name || '-'}</div>
                )}
              </div>
              <div className="col-md-6">
                <div className="client-detail-label">Client Slug</div>
                {isModalEditing ? (
                  <>
                    <input
                      type="text"
                      className="form-control"
                      value={modalForm.slug}
                      onChange={(e) =>
                        setModalForm((prev) => ({
                          ...prev,
                          slug: e.target.value.toLowerCase().replace(/\s+/g, '-'),
                        }))
                      }
                    />
                    {modalErrors.slug ? <div className="text-danger small mt-1">{modalErrors.slug}</div> : null}
                  </>
                ) : (
                  <div className="client-detail-value text-break">{selectedClient.client_slug || '-'}</div>
                )}
              </div>
              <div className="col-md-6">
                <div className="client-detail-label">Email</div>
                {isModalEditing ? (
                  <>
                    <input
                      type="email"
                      className="form-control"
                      value={modalForm.email}
                      onChange={(e) => setModalForm((prev) => ({ ...prev, email: e.target.value }))}
                    />
                    {modalErrors.email ? <div className="text-danger small mt-1">{modalErrors.email}</div> : null}
                  </>
                ) : (
                  <div className="client-detail-value text-break">
                    {selectedClient.admin_email || selectedClient.Email || '-'}
                  </div>
                )}
              </div>
              <div className="col-md-6">
                <div className="client-detail-label">Contact</div>
                {isModalEditing ? (
                  <>
                    <input
                      type="tel"
                      className="form-control"
                      value={modalForm.contact}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (/^\d{0,10}$/.test(value)) {
                          setModalForm((prev) => ({ ...prev, contact: value }));
                        }
                      }}
                    />
                    {modalErrors.contact ? <div className="text-danger small mt-1">{modalErrors.contact}</div> : null}
                  </>
                ) : (
                  <div className="client-detail-value">
                    {selectedClient.admin_mobile || selectedClient.ContactNumber || '-'}
                  </div>
                )}
              </div>
              <div className="col-md-6">
                <div className="client-detail-label">Type</div>
                {isModalEditing ? (
                  <select
                    className="form-select"
                    value={modalForm.role}
                    onChange={(e) => setModalForm((prev) => ({ ...prev, role: e.target.value }))}
                  >
                    <option value="T">Ticketbot</option>
                    <option value="L">Leadbot</option>
                    <option value="C">Client Admin</option>
                  </select>
                ) : (
                  <div className="client-detail-value">{getRoleLabel(selectedClient.admin_role)}</div>
                )}
              </div>
              <div className="col-md-6">
                {/* <div className="client-detail-label">Set Status</div> */}
                {isModalEditing ? (
                  <select
                    className="form-select"
                    value={modalForm.status || (hasAdminToken(selectedClient) ? 'active' : 'inactive')}
                    onChange={(e) => setModalForm((prev) => ({ ...prev, status: e.target.value }))}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                ) : (
                  <div className="client-detail-value">{getActivationState(selectedClient).label}</div>
                )}
              </div>
              <div className="col-md-6">
                <div className="client-detail-label">Registered On</div>
                <div className="client-detail-value">
                  {formatDate(getRegistrationDate(selectedClient))}
                </div>
              </div>
              <div className="col-md-12">
                <div className="client-detail-label">Activation Status</div>
                <div className="client-detail-value">
                  {getActivationState(selectedClient).label}
                </div>
              </div>
              <div className="col-md-6">
                <div className="client-detail-label">Employees</div>
                <div className="client-detail-value">{selectedClient.NoofEmp || 0}</div>
              </div>
            </div>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          {isModalEditing ? (
            <>
              <Button
                variant="outline-secondary"
                onClick={() => {
                  if (!selectedClient) return;
                  setIsModalEditing(false);
                  setModalErrors({ slug: '', email: '', contact: '' });
                  setModalForm({
                    name: selectedClient.Name || '',
                    slug: selectedClient.client_slug || '',
                    email: selectedClient.admin_email || selectedClient.Email || '',
                    contact: selectedClient.admin_mobile || selectedClient.ContactNumber || '',
                    role: selectedClient.admin_role || 'T',
                    status: hasAdminToken(selectedClient) ? 'active' : 'inactive',
                  });
                }}
              >
                Cancel
              </Button>
              <Button variant="primary" onClick={handleModalSave}>
                Save Changes
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline-secondary" onClick={closeClientModal}>
                Close
              </Button>
              <Button variant="primary" onClick={() => setIsModalEditing(true)}>
                Edit
              </Button>
            </>
          )}
        </Modal.Footer>
      </Modal>

      <style>{`
        .all-client-page {
          --page-bg: #f4f8ff;
          --surface: #ffffff;
          --soft-border: #e2e8f0;
          background:
            radial-gradient(circle at 5% 0%, rgba(14, 165, 233, 0.14), transparent 30%),
            radial-gradient(circle at 95% 20%, rgba(37, 99, 235, 0.12), transparent 35%),
            var(--page-bg);
          border-radius: 20px;
          padding-bottom: 8px;
        }

        .all-client-shell {
          border: 1px solid rgba(148, 163, 184, 0.22);
          backdrop-filter: blur(3px);
          box-shadow: 0 22px 45px rgba(15, 23, 42, 0.08);
        }

        .all-client-hero {
          background:
            radial-gradient(circle at top left, rgba(37, 99, 235, 0.14), transparent 36%),
            radial-gradient(circle at bottom right, rgba(16, 185, 129, 0.12), transparent 34%),
            linear-gradient(145deg, #ffffff 0%, #f8fbff 100%);
          border: 1px solid var(--soft-border);
          box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.8);
        }

        .all-client-stat-card {
          min-height: 78px;
        }

        .all-client-stat-label {
          font-size: 0.72rem;
          letter-spacing: 0.06em;
        }

        .all-client-stat-number {
          font-size: 1.9rem;
          line-height: 1.05;
        }

        .all-client-kicker {
          font-size: 0.72rem;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          font-weight: 800;
          color: #1d4ed8;
          margin-bottom: 6px;
        }

        .mobile-client-grid {
          display: grid;
          gap: 12px;
        }

        .mobile-client-card {
          border: 1px solid var(--soft-border);
          border-radius: 16px;
          background: var(--surface);
          padding: 14px;
          box-shadow: 0 10px 22px rgba(15, 23, 42, 0.06);
        }

        .all-client-data-zone {
          border: 1px solid rgba(148, 163, 184, 0.2);
          border-radius: 16px;
          background: #ffffff;
          padding: 8px;
          box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.65);
        }

        .all-client-table-wrap {
          border-radius: 12px;
          border: 1px solid #dbe4f0;
          overflow: hidden;
        }

        .all-client-table thead.table-dark th {
          background: linear-gradient(90deg, #0f172a 0%, #1e293b 100%);
          border-bottom-width: 0;
        }

        .all-client-table tbody tr {
          transition: background-color 0.2s ease;
        }

        .all-client-table th,
        .all-client-table td {
          padding-top: 0.55rem;
          padding-bottom: 0.55rem;
          vertical-align: middle;
        }

        .all-client-table tbody tr:hover {
          background-color: #eef6ff;
        }

        .table-feedback-layer {
          position: absolute;
          top: 16px;
          left: 0;
          right: 0;
          display: flex;
          justify-content: center;
          pointer-events: none;
          z-index: 4;
          padding: 0 12px;
        }

        .table-feedback-popup {
          width: min(520px, 100%);
          display: flex;
          align-items: center;
          gap: 12px;
          border-radius: 14px;
          padding: 12px 14px;
          border: 1px solid transparent;
          box-shadow: 0 20px 38px rgba(15, 23, 42, 0.16);
          color: #0f172a;
          backdrop-filter: blur(8px);
          pointer-events: auto;
          animation: feedback-pop-in 0.24s ease;
        }

        .table-feedback-popup-success {
          background: linear-gradient(140deg, rgba(220, 252, 231, 0.96) 0%, rgba(187, 247, 208, 0.97) 100%);
          border-color: rgba(34, 197, 94, 0.38);
        }

        .table-feedback-popup-danger {
          background: linear-gradient(140deg, rgba(254, 226, 226, 0.98) 0%, rgba(254, 202, 202, 0.98) 100%);
          border-color: rgba(239, 68, 68, 0.38);
        }

        .table-feedback-icon {
          width: 34px;
          height: 34px;
          border-radius: 50%;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          background: rgba(255, 255, 255, 0.75);
          flex-shrink: 0;
        }

        .table-feedback-content {
          flex: 1;
          min-width: 0;
        }

        .table-feedback-title {
          font-size: 0.84rem;
          font-weight: 800;
          letter-spacing: 0.05em;
          text-transform: uppercase;
          margin-bottom: 2px;
        }

        .table-feedback-text {
          font-size: 0.92rem;
          font-weight: 600;
          line-height: 1.3;
          word-break: break-word;
        }

        .table-feedback-close {
          border: 0;
          background: transparent;
          color: #334155;
          border-radius: 8px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 30px;
          height: 30px;
        }

        .table-feedback-close:hover {
          background: rgba(255, 255, 255, 0.66);
        }

        .mobile-client-card-inactive {
          border-color: rgba(239, 68, 68, 0.25);
          background: #fff8f8;
        }

        .mobile-client-meta {
          display: grid;
          gap: 6px;
        }

        .mobile-client-meta-item {
          display: flex;
          gap: 8px;
          align-items: center;
          color: #475569;
          font-size: 0.86rem;
        }

        .slug-col-head {
          min-width: 160px;
        }

        .slug-col-cell {
          min-width: 160px;
          max-width: 220px;
        }

        .client-slug-chip {
          display: inline-block;
          max-width: 100%;
          padding: 4px 8px;
          border-radius: 8px;
          background: #f1f5f9;
          color: #334155;
          font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
          font-size: 0.78rem;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          vertical-align: middle;
        }

        .client-detail-label {
          font-size: 0.75rem;
          color: #64748b;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          font-weight: 700;
          margin-bottom: 2px;
        }

        .client-detail-value {
          font-size: 0.95rem;
          color: #0f172a;
          font-weight: 600;
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 10px;
          padding: 9px 10px;
        }

        @media (max-width: 1199.98px) {
          .all-client-page .card-body {
            padding: 0.75rem !important;
          }

          .all-client-page .btn {
            min-height: 38px;
          }

          .all-client-data-zone {
            padding: 0;
            border: 0;
            box-shadow: none;
            background: transparent;
          }

          .all-client-stat-number {
            font-size: 1.5rem;
          }
        }

        @media (max-width: 575.98px) {
          .table-feedback-layer {
            top: 8px;
            padding: 0 6px;
          }

          .table-feedback-popup {
            border-radius: 10px;
            padding: 10px;
          }
        }

        @keyframes feedback-pop-in {
          from {
            opacity: 0;
            transform: translateY(-7px) scale(0.98);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
      `}</style>
    </div>
  );
};

export default AllClient;
